/* Print A Bill — edit-mode UI.
 *
 * Owns two things while the builder is in validated edit mode: the wording on
 * the four primary actions, and the visibility of the existing
 * "Cancel and return to cart" affordance.
 *
 * Why this is a separate asset with an observer rather than inline timeouts:
 * pab-mockup.js re-renders #simple-add and #add-cart on its own update cycle
 * and overwrites their text. A re-apply tail was tried and lost the race. That
 * file is minified and hash-pinned by check 10, so it cannot own the label —
 * an observer that reacts to its writes is the only way to hold the state
 * without touching it.
 *
 * Edit mode itself is decided in pab-mockup-bridge.js and only turns on for a
 * build id that came from an explicit ?pab_edit= URL and resolved against the
 * live cart. This module only reads it.
 */
(function () {
  'use strict';

  var SELECTORS = ['.add-to-cart-btn', '#simple-add', '#add-cart', '#mobile-cart'];
  var applying = false;   // re-entrancy guard: our own writes must not re-trigger

  function editActive() {
    return !!(window.PABEditMode && window.PABEditMode.active);
  }

  function buttons() {
    return SELECTORS
      .map(function (s) { return document.querySelector(s); })
      .filter(Boolean);
  }

  /* The desired label is derived from the button's own state, so a button
   * mid-submit keeps its processing word and is never yanked back to idle. */
  function desiredLabel(btn) {
    if (btn.classList.contains('success') || btn.classList.contains('added')) {
      return editActive() ? 'Updated \u2713' : 'Added \u2713';
    }
    if (btn.classList.contains('adding') || btn.disabled) {
      return editActive() ? 'Updating...' : 'Adding...';
    }
    return editActive() ? 'Update Cart' : 'Add to Cart';
  }

  function syncLabels() {
    if (applying) return;
    applying = true;
    try {
      buttons().forEach(function (btn) {
        var want = desiredLabel(btn);
        // Compare before writing, or the observer feeds itself forever.
        if (btn.textContent.trim() !== want) btn.textContent = want;
      });
    } finally {
      applying = false;
    }
  }

  /* The markup already exists in pab-mockup.liquid, hidden. Edit mode is the
   * only thing that should reveal it. */
  function syncCancelAffordance() {
    var host = document.getElementById('pab-edit-context');
    if (!host) return;
    host.hidden = !editActive();
  }

  function syncAll() {
    syncLabels();
    syncCancelAffordance();
  }

  var observer = null;

  function observe() {
    if (typeof MutationObserver !== 'function') return;
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () { syncLabels(); });
    buttons().forEach(function (btn) {
      observer.observe(btn, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled']
      });
    });
  }

  function init() {
    syncAll();
    observe();

    document.addEventListener('pab:edit-mode', function () {
      syncAll();
      // Buttons can be replaced wholesale by the mockup; rebind to the current
      // nodes rather than assuming the originals survived.
      observe();
    });

    /* Sticky bar and summary mount late and can be re-created on resize or
     * orientation change, so re-observe on the events that cause it. */
    ['resize', 'orientationchange'].forEach(function (evt) {
      window.addEventListener(evt, function () { syncAll(); observe(); }, { passive: true });
    });

    // Cheap safety net for re-mounts we do not have an event for.
    setInterval(function () { syncAll(); observe(); }, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.PABEditModeUI = { sync: syncAll, labelFor: desiredLabel };
})();
